/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { _isEmpty, _isNil, _isNotEmpty } from '@/littledash';
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import {
  useTable,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ExpandedState,
  type PaginationState,
  type RowData,
  type RowSelectionState,
} from '@tanstack/react-table';
import { List } from 'react-window';
import classNames from 'classnames';
import TableHeader from './Cells/TableHeader';
import Expander from './Columns/Expander';
import Selector from './Columns/Selector';
import Pagination from './PaginationNew';
import './Table.scss';
import { tableFeatureSet, type TableFeatureSet } from './Table.features';
import { plainStateSetter } from './Table.state';
import { type TanStackColumns } from './TableComponent.model';

const getStyles = (props: any, align = 'left', verticalAlign = ' ', show = true) => [
  props,
  {
    style: {
      justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
      alignItems: verticalAlign === 'top' ? 'flex-start' : verticalAlign === 'bottom' ? 'flex-end' : 'center',
      display: 'flex',
    },
  },
];
const headerProps = (props: any, { column }: { column: any }) => getStyles(props, column.align);
const noResultsMessage = 'Your search did not return any results';
const rowHeight = 50;
const maxTBodyHeight = 615;
const maxRowCountBeforeVirtualising = 11;
const scrollBarHeightAllowance = 15;

const calculateTBodyHeight = (rowCount: number) =>
  rowCount <= maxRowCountBeforeVirtualising ? rowCount * rowHeight + scrollBarHeightAllowance : maxTBodyHeight;

export interface TableProps<T extends RowData> {
  className?: string;
  columns: TanStackColumns<T>;
  data: Array<T>;
  selectedRows?: Record<number, boolean>;
  setSelectedRows?: (selectedRows: Record<number, boolean>) => void;
  pagination?: boolean;
  expandable?: boolean;
  manualSort?: boolean | { sort: string; order: string };
  setManualSort?: (value: string | { sort: string; order: string }) => void;
  sortBy?: Array<{ id: string; desc: boolean }>;
  noDataMessage?: string;
  noDataComponent?: ReactElement;
  hasContextMenu?: boolean;
  searchQuery?: string;
  initPageSize?: number;
  disableSelectAll?: boolean;
}

const Table = <T extends RowData>({
  columns,
  data,
  pagination: enablePagination = false,
  expandable = false,
  setSelectedRows,
  selectedRows,
  manualSort,
  setManualSort,
  sortBy = [],
  noDataMessage = 'No rows to display',
  noDataComponent,
  hasContextMenu,
  searchQuery,
  className = '',
  initPageSize,
  disableSelectAll = false,
}: TableProps<T>): ReactElement<any, any> | null => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>(sortBy?.map((sort) => ({ id: sort.id, desc: sort.desc })) || []);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const [rowSelection, setRowSelection] = useState<RowSelectionState>((selectedRows || {}) as RowSelectionState);
  const [pagination, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initPageSize ?? 10,
  });

  const [columnSizing, setColumnSizing] = useState({});

  // Column visibility state based on isVisible property
  const initialColumnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    columns.forEach((col: any) => {
      if (col?.isVisible === false) {
        visibility[col.id as string] = false;
      }
    });
    return visibility;
  }, [columns]);

  const [columnVisibility, setColumnVisibility] = useState(initialColumnVisibility);

  // Memoize expander column to prevent re-creation
  const expanderColumn = useMemo(() => {
    if (expandable) {
      return Expander();
    }
    return null;
  }, [expandable]);

  // Use a ref to store the stringified columns to detect actual changes
  const columnsStringRef = useRef<string>('');
  const columnsString = JSON.stringify(
    columns.map((c: any) => ({ id: c.id, accessor: c.accessor || c.accessorKey, accessorKey: c.accessorKey }))
  );

  const memorizedColumns = useMemo(() => {
    columnsStringRef.current = columnsString;

    let processedColumns = [...columns] as Array<ColumnDef<TableFeatureSet, T, unknown>>;

    if (setSelectedRows) {
      processedColumns = [Selector(false, setSelectedRows), ...processedColumns];
    }

    if (expanderColumn) {
      processedColumns = [expanderColumn as ColumnDef<TableFeatureSet, T, unknown>, ...processedColumns];
    }
    return processedColumns;
  }, [columnsString, expanderColumn, columns, setSelectedRows]);

  const table = useTable({
    features: tableFeatureSet,
    data,
    columns: memorizedColumns,

    // State
    state: {
      sorting,
      expanded,
      rowSelection,
      pagination,
      columnSizing,
      columnVisibility,
    },

    // State setters
    onSortingChange: setSorting,
    onExpandedChange: plainStateSetter(setExpanded),
    onRowSelectionChange: plainStateSetter(setRowSelection),
    onPaginationChange: setPaginationState,
    onColumnSizingChange: plainStateSetter(setColumnSizing),
    onColumnVisibilityChange: plainStateSetter(setColumnVisibility),

    // Options
    enableSorting: true,
    enableColumnResizing: true,
    enableHiding: true,
    columnResizeMode: 'onChange',
    enableExpanding: expandable,
    enableRowSelection: !!setSelectedRows,
    enableMultiRowSelection: true,
    manualSorting: !!manualSort,
    manualPagination: false,

    // For backward compatibility
    getRowId: (row, index) => String(index),
    getSubRows: (row) => (row as any)?.subRows,
    debugTable: false,
  });

  // Backward compatibility: extract needed values
  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const page = enablePagination ? table.getRowModel().rows : rows;
  const pageCount = table.getPageCount();
  const canPreviousPage = table.getCanPreviousPage();
  const canNextPage = table.getCanNextPage();
  const { pageIndex, pageSize } = pagination;
  const selectedRowIds = rowSelection;
  const expandedState = expanded;

  // Backward compatibility methods
  const gotoPage = table.setPageIndex;
  const nextPage = table.nextPage;
  const previousPage = table.previousPage;
  const setPageSize = table.setPageSize;
  const toggleAllRowsSelected = table.toggleAllRowsSelected;

  // Mock methods for backward compatibility
  const getTableBodyProps = () => ({});

  // Update column visibility when columns change
  useEffect(() => {
    setColumnVisibility(initialColumnVisibility);
  }, [initialColumnVisibility]);

  useEffect(() => {
    if (_isNil(initPageSize) && page.length !== data?.length && _isEmpty(expandedState)) {
      setPageSize(data.length);
    }
  }, [data, page.length, initPageSize, selectedRowIds, setPageSize, toggleAllRowsSelected, expandedState]);

  useEffect(() => {
    if (_isNotEmpty(expandedState)) {
      let newPageSize = initPageSize ?? data.length;
      Object.keys(expandedState).forEach((row) => {
        const rowData = data?.[Number(row)] as any;
        newPageSize = newPageSize + (rowData?.subRows?.length ?? 0);
      });
      setPageSize(newPageSize);
    }
  }, [expandedState, data, initPageSize, setPageSize]);

  useEffect(() => {
    if (setSelectedRows) {
      setSelectedRows(selectedRowIds);
    }
  }, [selectedRowIds, setSelectedRows]);

  const [rowWidth, setRowWidth] = useState<number>();
  const [tableWidth, setTableWidth] = useState<number>();

  const totalColumnsWidth = headerGroups.reduce(
    (total, headerGroup) =>
      total + headerGroup.headers.reduce((groupTotal, header) => groupTotal + header.getSize(), 0),
    0
  );

  useEffect(() => {
    const handleResize = () => {
      setTableWidth(tableContainerRef?.current?.scrollWidth ?? totalColumnsWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize, { capture: false, passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [totalColumnsWidth]);

  useEffect(() => {
    const currentWidth = totalColumnsWidth <= (tableWidth ?? 0) ? (tableWidth ?? totalColumnsWidth) : totalColumnsWidth;
    setRowWidth(currentWidth);
  }, [totalColumnsWidth, tableWidth]);

  const RenderRow = ({ index, style }: { index: number; style: any }) => {
    const row = page[index];
    return (
      <div
        style={style}
        className={classNames('tr', {
          'bb b--moon-gray': index === page.length - 1,
        })}
        role="row"
        data-test-component="RenderRow"
        data-test-element="row-container"
        data-test-key={row.id}
        data-testid={`api-table-row-${row.id}`}
      >
        {row.getVisibleCells().map((cell, cellIndex) => {
          const meta = cell.column.columnDef.meta as any;
          const cellStyle = getStyles({}, meta?.align, meta?.verticalAlign);

          return (
            <div
              className={classNames('td', meta?.className, { 'bl b--moon-gray': cellIndex !== 0 })}
              key={cell.id}
              role="cell"
              style={{
                ...(!meta?.className?.includes('ui__selector-column') && {
                  ...cellStyle[1]?.style,
                  ...meta?.style,
                  flex: `1 0 ${cell.column.getSize()}px`,
                }),
                minWidth: cell.column.columnDef.minSize,
                maxWidth:
                  meta?.className?.includes('ui__selector-column') || meta?.className?.includes('ui__expander-column')
                    ? cell.column.getSize()
                    : undefined,
              }}
              data-test-component="RenderRow"
              data-test-element="cell"
              data-test-key={`( ${cell.column.id} , ${index} )`}
              data-testid={`api-table-column-${cellIndex}`}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`ui__table ${className}`} data-test-component="Table" data-test-element="container">
      <div className="table overflow-auto" ref={tableContainerRef}>
        <div style={{ width: rowWidth ?? undefined }}>
          <div className="thead" role="rowgroup" data-test-element="header-container">
            {headerGroups.map((headerGroup, index) => (
              <div className="tr" role="row" key={`headerGroup_${index}`}>
                {headerGroup.headers.map((header, index) => (
                  <TableHeader
                    key={`header_${index}`}
                    header={header}
                    headerProps={headerProps}
                    manualSort={manualSort}
                    setManualSort={setManualSort}
                  />
                ))}
              </div>
            ))}
          </div>
          <div
            className={`${hasContextMenu ? 'context-menu' : ''} tbody`}
            role="rowgroup"
            {...getTableBodyProps()}
            data-test-element="body-container"
          >
            {_isEmpty(data) ? (
              _isNil(noDataComponent) ? (
                <div className="flex flex-column white min-h5 justify-center items-center tc pa3 ma3 bg-light-gray">
                  <h2 className="tc pa3 ma3">{searchQuery ? noResultsMessage : noDataMessage}</h2>
                </div>
              ) : (
                noDataComponent
              )
            ) : (
              <List<object>
                style={{ width: rowWidth || 0, height: calculateTBodyHeight(page.length) }}
                rowCount={page.length}
                rowHeight={rowHeight}
                rowComponent={RenderRow}
                rowProps={{}}
                className={page.length > maxRowCountBeforeVirtualising ? 'bb b--moon-gray' : ''}
              />
            )}
          </div>
        </div>
      </div>
      {enablePagination && _isNotEmpty(data) && (
        <Pagination
          previousPage={previousPage}
          nextPage={nextPage}
          pageCount={pageCount}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};

export default Table;
